import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import InlinePageSizeSelector from "../../components/CustomComponents/InlinePageSizeSelector/InlinePageSizeSelector";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import { appSettings } from "../../app/appSettingsSlice";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import "./ManageOrderFilters.css";
import axiosInstance from "../../config/axiosApi/axiosApi";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import {
  setVisibleColumns,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import { groupOrderItemsByModifierRelation } from "../../utils/orderItemsGroupingUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import {
  hasCancelAudit,
  labelForCancelReasonCode,
  pickOrderField,
} from "../../utils/marketplaceCancelReasonLabels";

const defaultItemFilter = [];

const formatProductQuantity = (qty) => {
  if (qty == null || qty === "") return "—";
  const n = parseFloat(qty);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  return n % 1 === 0 ? String(n) : n.toFixed(2);
};

const getProductDisplayName = (p) => {
  const variantTitle = (p?.menuItem_DefaultVariantTitle ?? p?.MenuItem_DefaultVariantTitle ?? "").toString().trim();
  const name = (p?.menuItem_Name ?? p?.MenuItem_Name ?? "").toString().trim();
  if (variantTitle) return variantTitle;
  const isVariant = p?.MenuItem_Type === "Variant" || p?.menuItem_Type === "Variant";
  const id = p?.menuItem_ID ?? p?.MenuItem_ID;
  if (isVariant && id != null && id !== "") return `${name || "Variant"} (#${id})`;
  return name || `Product #${id ?? ""}`;
};

const manageOrderColumnDefinitions = {
  frontorderid: "Front Order ID",
  orderType: "Order Type",
  orderStatus: "Order Status",
  orderSendBy: "Order Send By",
  rider: "Rider",
  invoiceNo: "Invoice No",
  source: "Source",
  tillname: "Till Name",
  customer: "Customer",
  orderDate: "Order Date",
  paymentStatus: "Payment Status",
  paymentMethod: "Payment Method",
  totalAmount: "Total Amount",
  productQty: "Product Qty",
  fillBy: "Fill By",
  marketplaceWastageAmount: "Wastage",
  marketplaceDeliveryMode: "Delivery Mode",
  marketplaceDiscountFundedByPlatform: "Discount (Platform)",
  marketplaceVoucherFundedByPlatform: "Voucher (Platform)",
  marketplacePlatformCommissionAmount: "Commission Amount",
  marketplacePlatformCommissionPercent: "Commission %",
  marketplaceWastageRefundAmount: "Wastage Refund",
};

const ManageOrder = () => {
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const user = useSelector((state) => state.auth.user);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const { getCompCurrency } = useProfileAndSettings();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();



  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [originalOrdersCount, setOriginalOrdersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailedOrder, setDetailedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const compCurrency = getCompCurrency();
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showItemFilterModal, setShowItemFilterModal] = useState(false);
  const [filterProducts, setFilterProducts] = useState([]);
  const [loadingFilterProducts, setLoadingFilterProducts] = useState(false);
  const [productSummary, setProductSummary] = useState([]);
  const lastSelectedIndex = useRef(null);
  const pageKey = "orders";
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const defaultLocation = useMemo(() => {
    if (isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)) {
      return { value: parentIdForLocation, label: companyName };
    }
    return { value: "all", label: "All Locations" };
  }, [isMain, parentIdForLocation, companyName]);
  const defaultVisibleColumns = {
    frontorderid: true,
    orderType: true,
    orderStatus: true,
    orderSendBy: true,
    rider: true,
    invoiceNo: true,
    source: true,
    tillname: false,
    customer: true,
    orderDate: true,
    paymentStatus: true,
    paymentMethod: true,
    totalAmount: true,
    fillBy: true,
    productQty: true,
    marketplaceWastageAmount: false,
    marketplaceDeliveryMode: false,
    marketplaceDiscountFundedByPlatform: false,
    marketplaceVoucherFundedByPlatform: false,
    marketplacePlatformCommissionAmount: false,
    marketplacePlatformCommissionPercent: false,
    marketplaceWastageRefundAmount: false,
  };
  const defaultType = useMemo(() => ({ value: "", label: "All" }), []);
  const defaultPaymentStatus = useMemo(() => ({ value: "", label: "All" }), []);
  const defaultOrderStatus = useMemo(() => ({ value: "", label: "All" }), []);
  const defaultMarketplaceFilter = useMemo(() => ({ value: "", label: "All" }), []);
  // Default to "This Week" if no existing filter (memoized to avoid new refs every render and prevent reload loop)
  const defaultDateRange = useMemo(
    () => [
      moment().startOf("week").toDate(),
      moment().endOf("week").toDate(),
    ],
    []
  );
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = preferences.visibleColumns || defaultVisibleColumns;

  // Use the actual visibleColumns for table display
  const safeVisibleColumns = visibleColumns;


  const type = preferences.type || defaultType;
  const paymentStatus = preferences.paymentStatus || defaultPaymentStatus;
  const OrderStatus = preferences.OrderStatus || defaultOrderStatus;
  const marketplaceFilter = preferences.marketplaceFilter || defaultMarketplaceFilter;
  const location = preferences.location || defaultLocation;
  // Use existing dateRange from Redux if present; coerce cleared/null dates back to "This Week"
  const dateRange = useMemo(() => {
    const stored = preferences.dateRange;
    if (!Array.isArray(stored)) return defaultDateRange;
    const startOk = stored[0] != null && moment(stored[0]).isValid();
    const endOk = stored[1] != null && moment(stored[1]).isValid();
    if (!startOk && !endOk) return defaultDateRange;
    return [
      startOk
        ? typeof stored[0] === "string"
          ? new Date(stored[0])
          : stored[0]
        : defaultDateRange[0],
      endOk
        ? typeof stored[1] === "string"
          ? new Date(stored[1])
          : stored[1]
        : defaultDateRange[1],
    ];
  }, [preferences.dateRange, defaultDateRange]);
  const itemFilter = Array.isArray(preferences.itemFilter) ? preferences.itemFilter : defaultItemFilter;
  const [startDate, endDate] = dateRange;

  const startDateKey = useMemo(() => {
    if (startDate == null) return "";
    const d = typeof startDate === "string" ? new Date(startDate) : startDate;
    return moment(d).isValid() ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "";
  }, [startDate]);

  const endDateKey = useMemo(() => {
    if (endDate == null) return "";
    const d = typeof endDate === "string" ? new Date(endDate) : endDate;
    return moment(d).isValid() ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "";
  }, [endDate]);

  const typeValue = type?.value ?? "";
  const paymentStatusValue = paymentStatus?.value ?? "";
  const orderStatusValue = OrderStatus?.value ?? "";
  const marketplaceFilterValue =
    marketplaceFilter?.value === true || marketplaceFilter?.value === "true"
      ? true
      : marketplaceFilter?.value === false || marketplaceFilter?.value === "false"
        ? false
        : null;
  const locationValue = location?.value ?? "all";
  const pageSizeValue = pageSize?.value ?? "50";

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const formatMarketplaceCurrency = (value) => {
    if (value == null || value === "" || parseFloat(value) === 0) return "—";
    return `${getCurrencySymbol(compCurrency)}${parseFloat(value).toFixed(2)}`;
  };

  const formatMarketplacePercent = (value) => {
    if (value == null || value === "" || parseFloat(value) === 0) return "—";
    return `${parseFloat(value).toFixed(2)}%`;
  };

  const formatMarketplaceText = (value) => {
    if (!value || String(value).trim() === "") return "—";
    return value;
  };

  const getMarketplaceField = (order, key) => {
    if (!order) return null;
    return order[key] ?? null;
  };

  const renderMarketplaceGridValue = (order, key, formatter) => {
    if (!order?.isMarketPlaceOrder) return "—";
    return formatter(getMarketplaceField(order, key));
  };

  // Currency fetching now handled by useProfileAndSettings hook

  // Error boundary for ResizeObserver
  useEffect(() => {
    // Override ResizeObserver to suppress the error
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (error) {
            if (error.message && error.message.includes('ResizeObserver')) {
              // Suppress ResizeObserver errors
              return;
            }
            throw error;
          }
        });
      }
    };

    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (
        e.reason &&
        e.reason.message &&
        e.reason.message.includes("ResizeObserver")
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add multiple event listeners to catch the error at different levels
    window.addEventListener("error", handleResizeObserverError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);

    // Also override console.error to suppress ResizeObserver errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return; // Suppress ResizeObserver console errors
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      // Restore original ResizeObserver
      window.ResizeObserver = OriginalResizeObserver;
      window.removeEventListener("error", handleResizeObserverError, true);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
      console.error = originalConsoleError;
    };
  }, []);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      const timeouts = [];
      const originalSetTimeout = window.setTimeout;
      window.setTimeout = (...args) => {
        const id = originalSetTimeout(...args);
        timeouts.push(id);
        return id;
      };

      // Clear all timeouts on unmount
      timeouts.forEach(id => clearTimeout(id));

      // Clear any pending API requests
      if (window.AbortController) {
        // This will be handled by axios interceptors if configured
      }

      // Restore body overflow on unmount
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    }
    if (!preferences.type) {
      dispatch(setFilterField({ pageKey, field: "type", value: defaultType }));
    }
    if (!preferences.paymentStatus) {
      dispatch(
        setFilterField({
          pageKey,
          field: "paymentStatus",
          value: defaultPaymentStatus,
        })
      );
    }
    if (!preferences.OrderStatus) {
      dispatch(
        setFilterField({
          pageKey,
          field: "OrderStatus",
          value: defaultOrderStatus,
        })
      );
    }
    if (!preferences.marketplaceFilter) {
      dispatch(
        setFilterField({
          pageKey,
          field: "marketplaceFilter",
          value: defaultMarketplaceFilter,
        })
      );
    }
    if (!preferences.dateRange) {
      dispatch(
        setFilterField({ pageKey, field: "dateRange", value: defaultDateRange })
      );
    }
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    if (preferences.itemFilter === undefined) {
      dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    }
  }, [dispatch, pageKey, defaultLocation, defaultVisibleColumns, defaultType, defaultPaymentStatus, defaultOrderStatus, defaultMarketplaceFilter, defaultDateRange]);

  const lastAccountKeyRef = useRef(null);

  const typeOptions = [
    { value: "", label: "All" },
    { value: "Delivery", label: "Delivery" },
    { value: "Collection", label: "Pickup" },
    { value: "Dine-In", label: "Dine In" },
  ];



  const paymentStatusOptions = [
    { value: "", label: "All" },
    { value: "Pending", label: "Unpaid" },
    { value: "Completed", label: "Paid" },
  ];

  const marketplaceFilterOptions = [
    { value: "", label: "All" },
    { value: true, label: "Marketplace" },
    { value: false, label: "POS / Other" },
  ];

  const OrderStatusOptions = [
    { value: "", label: "All" },
    { value: "Pending", label: "Pending" },
    { value: "Ready", label: "Ready" },
    { value: "In Process", label: "In Process" },
    { value: "Pre Order", label: "Pre Order" },
    { value: "Refunded", label: "Refunded" },
    { value: "RefundReturn", label: "Refunded (returned to stock)" },
    { value: "Served", label: "Served" },
    { value: "Rejected", label: "Rejected" },
    { value: "Completed", label: "Completed" },
    { value: "On the Way", label: "On the Way" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  const fetchCustomerDetails = useCallback(
    async (custGuid) => {
      if (!custGuid || !CompID) {
        setCustomerDetails(null);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get(APIs.GET_CUSTOMER_BY_GUID, {
          params: { CompID, Cust_Guid: custGuid },
        });

        const data = response.data.data || response.data;
        setCustomerDetails({
          Cust_Fname: data.cust_Fname || "",
          Cust_Lname: data.cust_Lname || "",
          Cust_Email: data.cust_Email || "",
          Cust_Mobile: data.cust_Mobile || "",
          Cust_Address: data.cust_Address || "",
        });
      } catch (error) {
        showErrorToast("Error fetching customer details");
        setCustomerDetails(null);
      } finally {
        setLoading(false);
      }
    },
    [CompID]
  );

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const base = [{ value: parentId, label: companyName }];
      // Include all descendant branches (direct children and sub-branches), not only direct children
      const branchOpts = arr
        .filter((loc) => {
          const id = loc.CompID ?? loc.compID;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (idNum !== parentId) return true; // any branch other than main
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      const allBranchesOption = { value: "all", label: "All Branches" };
      setLocations([allBranchesOption, ...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, companyName]);

  useEffect(() => {
    if (isMain) fetchLocations();
  }, [isMain, fetchLocations]);

  const fetchFilterProducts = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoadingFilterProducts(true);
      const response = await axiosInstance.get(APIs.GET_ALL_PRODUCTS_FOR_PURCHASE_ORDER, {
        params: { CompID },
      });
      const data = response.data?.data ?? response.data?.Data ?? response.data ?? [];
      setFilterProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching products for filter:", error);
      showErrorToast(error.response?.data?.message || "Error loading products");
      setFilterProducts([]);
    } finally {
      setLoadingFilterProducts(false);
    }
  }, [CompID]);

  useEffect(() => {
    if (showItemFilterModal) fetchFilterProducts();
  }, [showItemFilterModal, fetchFilterProducts]);

  const handleItemFilterSubmit = useCallback(
    (newList) => {
      dispatch(setFilterField({ pageKey, field: "itemFilter", value: newList ?? [] }));
      setShowItemFilterModal(false);
      setPageNumber(1);
    },
    [dispatch, pageKey]
  );

  const clearItemFilter = useCallback(() => {
    dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    setProductSummary([]);
    setPageNumber(1);
  }, [dispatch, pageKey]);

  const productSummaryGrandTotal = useMemo(
    () =>
      productSummary.reduce(
        (sum, row) => sum + (parseFloat(row.totalQuantity ?? row.TotalQuantity) || 0),
        0
      ),
    [productSummary]
  );

  // When user/account changes, reset branch filter and list state (avoids stale filters + reload loops)
  useEffect(() => {
    if (!CompID) return;
    const accountKey = `${userIdentity}:${CompID}`;
    if (lastAccountKeyRef.current === accountKey) return;
    lastAccountKeyRef.current = accountKey;

    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    setProductSummary([]);
    setPageNumber(1);
    setSelectedOrders(new Set());
    setSearch("");
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, defaultLocation, dispatch, pageKey, isMain, fetchLocations]);

  const fetchOrderByInvoiceNo = useCallback(
    async (invoiceNo) => {
      if (!invoiceNo || !CompID) return;

      try {
        setLoading(true);
        const response = await axiosInstance.get(APIs.GET_ORDER_BY_INVOICE_NO, {
          params: { CompID, InvoiceNo: invoiceNo },
        });

        if (response.data.status) {
          setDetailedOrder(response.data.data);
        } else {
          showErrorToast(
            response.data.message || "Failed to fetch order details"
          );
          setDetailedOrder(null);
        }
      } catch (error) {
        showErrorToast("Error fetching order details");
        setDetailedOrder(null);
      } finally {
        setLoading(false);
      }
    },
    [CompID]
  );

  const fetchOrdersData = useCallback(async () => {
    if (!CompID) {
      setOrders([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const formattedStartDate = startDateKey || null;
      const formattedEndDate = endDateKey || null;

      const locationFilterValue =
        isMain && locationValue != null && locationValue !== "all"
          ? typeof locationValue === "number"
            ? locationValue
            : parseInt(locationValue, 10)
          : null;
      const locationFilterParam =
        !isNaN(locationFilterValue) && locationFilterValue > 0 ? locationFilterValue : null;

      const selectedItemIds =
        itemFilter.length > 0
          ? itemFilter
              .map((p) => {
                const id = p?.menuItem_ID ?? p?.MenuItem_ID;
                return typeof id === "number" ? id : parseInt(id, 10);
              })
              .filter((id) => !isNaN(id) && id > 0)
          : [];
      const itemIdsParam = selectedItemIds.length > 0 ? selectedItemIds.join(",") : undefined;

      const response = await axiosInstance.get(APIs.GET_ALL_ORDERS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          PageSize: pageSizeValue,
          PageNumber: pageNumber,
          Start: formattedStartDate,
          End: formattedEndDate,
          OrderType: typeValue,
          PaymentStatus: paymentStatusValue,
          OrderStatus: orderStatusValue,
          LocationFilter: locationFilterParam,
          ItemIds: itemIdsParam,
          ...(marketplaceFilterValue !== null
            ? { IsMarketPlaceOrder: marketplaceFilterValue }
            : {}),
        },
      });

      const ordersData = response.data.data || response.data;
      const summaryData =
        response.data.productSummary ?? response.data.ProductSummary ?? [];
      setProductSummary(
        selectedItemIds.length > 0 && Array.isArray(summaryData) ? summaryData : []
      );

      setTotalPages(response.data.totalPages || 0);
      setTotalRecords(response.data.totalRecords || 0);
      if (Array.isArray(ordersData)) {
        setOrders(ordersData);
        if (!search.trim()) {
          setOriginalOrdersCount(ordersData.length);
        }
      } else {
        setOrders([]);
      }
    } catch (err) {
      showErrorToast("Error fetching orders");
    } finally {
      setLoading(false);
    }
  }, [
    CompID,
    search,
    startDateKey,
    endDateKey,
    typeValue,
    paymentStatusValue,
    orderStatusValue,
    marketplaceFilterValue,
    locationValue,
    isMain,
    pageSizeValue,
    pageNumber,
    itemFilter,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrdersData();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchOrdersData]);

  const handleDelete = async (order) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete Order #${order.recvorder_ID}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!CompID || !order.recvorder_ID) {
        showErrorToast("Invalid Company ID or Order ID");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_ORDER, {
        params: {
          CompID,
          RecvOrder_ID: order.recvorder_ID,
        },
      });

      if (response.status === 200 && response.data.status) {
        setOrders((prevOrders) =>
          prevOrders.filter((item) => item.recvorder_ID !== order.recvorder_ID)
        );
        showSuccessToast("Order is Deleted.");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.Message || "Something Went Wrong.");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(
      setFilterField({ pageKey, field: "dateRange", value: defaultDateRange })
    );
    dispatch(setFilterField({ pageKey, field: "type", value: defaultType }));
    dispatch(
      setFilterField({
        pageKey,
        field: "paymentStatus",
        value: defaultPaymentStatus,
      })
    );
    dispatch(
      setFilterField({
        pageKey,
        field: "OrderStatus",
        value: defaultOrderStatus,
      })
    );
    dispatch(
      setFilterField({
        pageKey,
        field: "marketplaceFilter",
        value: defaultMarketplaceFilter,
      })
    );
    if (isMain) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    setProductSummary([]);
    setPageNumber(1);
  };

  const handleSelectOrder = (orderId, index, event) => {
    setSelectedOrders((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);

        for (let i = start; i <= end; i++) {
          if (orders[i]?.recvorder_ID) {
            newSelected.add(orders[i].recvorder_ID);
          }
        }
      } else {
        if (newSelected.has(orderId)) {
          newSelected.delete(orderId);
        } else {
          newSelected.add(orderId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(orders.map((order) => order.recvorder_ID));
      setSelectedOrders(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedOrders(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkComplete = async () => {
    if (selectedOrders.size === 0) {
      showErrorToast("No orders selected for completion.");
      return;
    }

    try {
      const confirmComplete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to mark ${selectedOrders.size} selected orders as completed?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmComplete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedOrders).map((orderId) =>
          axiosInstance.post(APIs.POST_UPDATE_ORDER_STATUS, {
            CompID,
            RecvOrder_ID: orderId,
            OrderStatus: "Completed",
          })
        )
      );

      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
      fetchOrdersData();
      showSuccessToast(`${selectedOrders.size} orders marked as completed.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to complete selected orders.");
      setLoading(false);
    }
  };

  const handleBulkPay = async () => {
    if (selectedOrders.size === 0) {
      showErrorToast("No orders selected for payment.");
      return;
    }

    try {
      const confirmPay = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to mark ${selectedOrders.size} selected orders as paid?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmPay) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedOrders).map((orderId) =>
          axiosInstance.post(APIs.POST_UPDATE_ORDER_PAYMENT, {
            CompID,
            RecvOrder_ID: orderId,
            PaymentStatus: "Paid",
          })
        )
      );

      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
      fetchOrdersData();
      showSuccessToast(`${selectedOrders.size} orders marked as paid.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to update payment status for selected orders.");
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) {
      showErrorToast("No orders selected for deletion.");
      return;
    }

    // Filter orders that can be deleted (only Completed or Draft)
    const deletableOrders = orders.filter(
      (order) =>
        selectedOrders.has(order.recvorder_ID) &&
        (order.recvorder_status?.trim() === "Completed" ||
          order.recvorder_status?.trim() === "Draft")
    );

    if (deletableOrders.length === 0) {
      showErrorToast("No deletable orders selected. Only Completed or Draft orders can be deleted.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${deletableOrders.length} selected order(s)?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        deletableOrders.map((order) =>
          axiosInstance.delete(APIs.DELETE_ORDER, {
            params: {
              CompID,
              RecvOrder_ID: order.recvorder_ID,
            },
          })
        )
      );

      setSelectedOrders(new Set());
      lastSelectedIndex.current = null;
      fetchOrdersData();
      showSuccessToast(`${deletableOrders.length} orders deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected orders.");
      setLoading(false);
    }
  };

  const renderNoDataMessage = () => {
    if (orders.length === 0 && itemFilter.length > 0) {
      return (
        <div className="alert alert-info text-center">
          No orders found containing any of the selected products for the current filters.
        </div>
      );
    }
    if (search.trim() && orders.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No orders found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  const handleOpenModal = (order) => {
    setShowModal(true);
    setSelectedOrder(order);
    setCustomerDetails(null);
    setDetailedOrder(null);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    if (order.recvorder_InvoiceNo) {
      fetchOrderByInvoiceNo(order.recvorder_InvoiceNo);
    }
    if (order.cust_Guid) {
      fetchCustomerDetails(order.cust_Guid);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setCustomerDetails(null);
    setDetailedOrder(null);
    // Restore body scroll when modal is closed
    document.body.style.overflow = 'unset';
  };

  const handleRowClick = (order) => {
    if (order.recvorder_InvoiceNo) {
      navigate(`/Order/orderDetails/${order.recvorder_InvoiceNo}`);
    }
  };

  const statusStyles = {
    Ready: { border: "border-info", text: "text-info", label: "Ready" },
    "In Process": {
      border: "border-warning",
      text: "text-warning",
      label: "In Process",
    },
    "Pre Order": {
      border: "border-secondary",
      text: "text-secondary",
      label: "Pre Order",
    },
    Refunded: { border: "border-danger", text: "text-danger", label: "Refunded" },
    RefundReturn: {
      border: "border-danger",
      text: "text-danger",
      label: "Refunded (returned to stock)",
    },
    Pending: {
      border: "border-warning",
      text: "text-warning",
      label: "Pending",
    },
    Served: { border: "border-success", text: "text-success", label: "Served" },
    Rejected: {
      border: "border-danger",
      text: "text-danger", label: "Rejected",
    },
    Completed: {
      border: "border-success",
      text: "text-success",
      label: "Completed",
    },
    "On the Way": {
      border: "border-primary",
      text: "text-primary",
      label: "On the Way",
    },
    Cancelled: {
      border: "border-danger",
      text: "text-danger",
      label: "Cancelled",
    },
    default: {
      border: "border-secondary",
      text: "text-secondary",
      label: "Not Paid",
    },
  };

  const paymentStatusStyles = {
    Paid: {
      border: "border-success",
      text: "text-success",
      label: "Paid",
    },
    "Un-Paid": {
      border: "border-danger",
      text: "text-danger",
      label: "Unpaid",
    },
    default: {
      border: "border-secondary",
      text: "text-secondary",
      label: "Unpaid",
    },
  };

  const paymentMethodStyles = {
    Card: {
      border: "border-info",
      text: "text-info",
      label: "Card",
    },
    Cash: {
      border: "border-primary",
      text: "text-primary",
      label: "Cash",
    },
    Online: {
      border: "border-info",
      text: "text-info",
      label: "Online",
    },
    default: {
      border: "border-secondary",
      text: "text-secondary",
      label: "N/A",
    },
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
    // Picker clear → restore default "This Week" (same as Clear filters)
    if (
      (!newStartDate || !moment(newStartDate).isValid()) &&
      (!newEndDate || !moment(newEndDate).isValid())
    ) {
      dispatch(
        setFilterField({ pageKey, field: "dateRange", value: defaultDateRange })
      );
      setPageNumber(1);
      return;
    }
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      let effectiveStartDate = newStartDate;
      let effectiveEndDate = newEndDate;
      if (newStartDate && newEndDate && newStartDate > newEndDate) {
        [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
      } else if (newStartDate && !newEndDate) {
        effectiveEndDate = new Date(newStartDate.getTime());
        effectiveEndDate.setHours(23, 59, 59, 999);
      }
      dispatch(
        setFilterField({
          pageKey,
          field: "dateRange",
          value: [effectiveStartDate, effectiveEndDate],
        })
      );
      setPageNumber(1);
    } else {
      showErrorToast("Invalid date selected");
    }
  };

  return (
    <ErrorBoundary>
      <div>
        {loading && <Preloader />}
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Manage Order</li>
            </ol>
            <h1 className="page-header mb-0">Manage Order</h1>
          </div>
        </div>

        <div className="card border-0" style={{ position: "relative", zIndex: 1 }}>
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Action Bar - Smooth Animation */}
              <div
                className="mb-3 overflow-hidden"
                style={{
                  height: selectedOrders.size > 0 ? "60px" : "0px",
                  transition: "height 0.3s ease-in-out",
                  opacity: selectedOrders.size > 0 ? "1" : "0",
                  transitionProperty: "height, opacity",
                  transitionDuration: "0.3s",
                  transitionTimingFunction: "ease-in-out"
                }}
              >
                <div className="p-3 bg-light rounded h-100 d-flex align-items-center" style={{ border: "1px solid #dee2e6" }}>
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedOrders.size} order{selectedOrders.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={handleBulkComplete}
                        title="Mark as Completed"
                      >
                        <i className="fa fa-check me-1"></i>
                        Complete
                      </button>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={handleBulkPay}
                        title="Mark as Paid"
                      >
                        <i className="fa fa-credit-card me-1"></i>
                        Mark Paid
                      </button>
                      {(() => {
                        // Check if any selected orders are deletable (Completed or Draft)
                        const hasDeletableOrders = Array.from(selectedOrders).some((orderId) => {
                          const order = orders.find((o) => o.recvorder_ID === orderId);
                          const orderStatus = order?.recvorder_status?.trim() || "";
                          return orderStatus === "Completed" || orderStatus === "Draft";
                        });
                        return hasDeletableOrders ? (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={handleBulkDelete}
                            title="Delete Selected Orders"
                          >
                            <i className="fa fa-trash me-1"></i>
                            Delete
                          </button>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Filter Section */}
              <div className="mb-4" style={{ position: 'relative', zIndex: 1000 }}>
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1000,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* First Row - Main Filters */}
                    <div className="row g-3 align-items-center mb-3">

                      {/* Date Range Picker */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <div style={{ position: 'relative', zIndex: 9999 }}>
                            <DateRangePicker
                              startDate={startDate && moment(startDate).isValid() ? (typeof startDate === "string" ? new Date(startDate) : startDate) : null}
                              endDate={endDate && moment(endDate).isValid() ? (typeof endDate === "string" ? new Date(endDate) : endDate) : null}
                              onChange={handleDateRangeChange}
                              placeholder="Select Date Range"
                              className="filter-dropdown"
                              allowSingleDate={true}
                              allowDateRange={true}
                              showTimeSelection={true}
                              style={{ 
                                zIndex: 9999,
                                width: '100%'
                              }}
                              dropdownStyle={{
                                width: '100%',
                                minWidth: '300px',
                                zIndex: 9999
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Branch dropdown - main company only */}
                      {isMain && locations.length > 0 && (
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={location}
                              onChange={(option) => {
                                dispatch(
                                  setFilterField({
                                    pageKey,
                                    field: "location",
                                    value: option,
                                  })
                                );
                                setPageNumber(1);
                              }}
                              options={locations}
                              styles={dropdownStyles}
                              placeholder="Branch"
                              className="filter-dropdown"
                              isLoading={loadingLocations}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>
                      )}

                      {/* Search Field */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setPageNumber(1);
                            }}
                            placeholder="Search by invoice, customer, table..."
                            style={{
                              backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.95)' : 'rgba(255, 255, 255, 0.9)',
                              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid rgba(255, 255, 255, 0.3)',
                              color: isDarkMode ? '#f1f3f5' : '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Order Status Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={OrderStatus}
                            onChange={(val) => {
                              dispatch(
                                setFilterField({
                                  pageKey,
                                  field: "OrderStatus",
                                  value: val,
                                })
                              );
                              setPageNumber(1);
                            }}
                            options={OrderStatusOptions}
                            styles={dropdownStyles}
                            placeholder="Order Status"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Order Type Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={type}
                            onChange={(val) => {
                              dispatch(
                                setFilterField({ pageKey, field: "type", value: val })
                              );
                              setPageNumber(1);
                            }}
                            options={typeOptions}
                            styles={dropdownStyles}
                            placeholder="Order Type"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={paymentStatus}
                            onChange={(val) => {
                              dispatch(
                                setFilterField({
                                  pageKey,
                                  field: "paymentStatus",
                                  value: val,
                                })
                              );
                              setPageNumber(1);
                            }}
                            options={paymentStatusOptions}
                            styles={dropdownStyles}
                            placeholder="Payment Status"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Marketplace filter */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={marketplaceFilter}
                            onChange={(val) => {
                              dispatch(
                                setFilterField({
                                  pageKey,
                                  field: "marketplaceFilter",
                                  value: val || defaultMarketplaceFilter,
                                })
                              );
                              setPageNumber(1);
                            }}
                            options={marketplaceFilterOptions}
                            styles={dropdownStyles}
                            placeholder="Marketplace"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                    </div>

                    {/* Second Row - Product filter & actions */}
                    <div className="row g-3 align-items-center">
                      <div className="col-xl-6 col-lg-6 col-md-12">
                        <div className="filter-control d-flex align-items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setShowItemFilterModal(true)}
                            disabled={loadingFilterProducts}
                            style={{
                              backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.95)" : "rgba(255, 255, 255, 0.95)",
                              border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.25)" : "1px solid rgba(255, 255, 255, 0.5)",
                              color: isDarkMode ? "#f1f3f5" : "#333",
                              fontWeight: 500,
                            }}
                            title="Filter orders by products"
                          >
                            <i className="fa fa-list me-1"></i>
                            {itemFilter.length > 0
                              ? `${itemFilter.length} product(s) selected`
                              : "Select products"}
                          </button>
                          {itemFilter.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm p-1"
                              onClick={clearItemFilter}
                              title="Clear product filter"
                              style={{
                                backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.8)",
                                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.25)" : "1px solid rgba(255, 255, 255, 0.5)",
                                color: isDarkMode ? "#f1f3f5" : "#333",
                              }}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="col-xl-6 col-lg-6 col-md-12">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                            columnDefinitions={manageOrderColumnDefinitions}
                            columnOrder={[
                              'frontorderid',
                              'orderType',
                              'orderStatus',
                              'orderSendBy',
                              'rider',
                              'invoiceNo',
                              'source',
                              'tillname',
                              'customer',
                              'orderDate',
                              'paymentStatus',
                              'paymentMethod',
                              'totalAmount',
                              'productQty',
                              'fillBy',
                              'marketplaceWastageAmount',
                              'marketplaceDeliveryMode',
                              'marketplaceDiscountFundedByPlatform',
                              'marketplaceVoucherFundedByPlatform',
                              'marketplacePlatformCommissionAmount',
                              'marketplacePlatformCommissionPercent',
                              'marketplaceWastageRefundAmount',
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {itemFilter.length > 0 && (
                <div
                  className={`manage-order-product-summary mb-3 ${isDarkMode ? "manage-order-product-summary-dark" : ""}`}
                  style={{ borderColor: themeColor }}
                >
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <h6 className="mb-0 fw-bold">
                      Product sales summary
                      <span className="fw-normal ms-1 manage-order-product-summary-meta">
                        (orders containing any selected product)
                      </span>
                    </h6>
                    <span className="manage-order-product-summary-total">
                      Grand total: <strong>{formatProductQuantity(productSummaryGrandTotal)}</strong>
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {productSummary.length > 0 ? (
                      productSummary.map((row) => {
                        const id = row.menuItem_ID ?? row.MenuItem_ID;
                        const filterItem = itemFilter.find(
                          (p) => (p?.menuItem_ID ?? p?.MenuItem_ID) === id
                        );
                        const label =
                          row.menuItem_Name ??
                          row.MenuItem_Name ??
                          (filterItem ? getProductDisplayName(filterItem) : `Product #${id}`);
                        const qty = row.totalQuantity ?? row.TotalQuantity;
                        return (
                          <span key={id} className="manage-order-product-summary-chip">
                            {label}: <strong>{formatProductQuantity(qty)}</strong>
                          </span>
                        );
                      })
                    ) : (
                      itemFilter.map((p) => {
                        const id = p?.menuItem_ID ?? p?.MenuItem_ID;
                        return (
                          <span key={id} className="manage-order-product-summary-chip">
                            {getProductDisplayName(p)}: <strong>0</strong>
                          </span>
                        );
                      })
                    )}
                  </div>
                  <p className="mb-0 mt-2 small text-muted manage-order-product-summary-meta">
                    Across {totalRecords} order{totalRecords !== 1 ? "s" : ""}
                    {startDate && moment(startDate).isValid()
                      ? ` · ${moment(startDate).format("DD MMM YYYY")}`
                      : ""}
                    {endDate && moment(endDate).isValid()
                      ? ` – ${moment(endDate).format("DD MMM YYYY")}`
                      : ""}
                  </p>
                </div>
              )}

              {renderNoDataMessage()}

              <div className="table-responsive" style={{ minHeight: "200px", position: "relative", zIndex: 1 }}>
                <table className="table table-hover" style={{ position: "relative", zIndex: 1 }}>
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onClick={(e) => e.stopPropagation()}
                          onChange={handleSelectAll}
                          checked={
                            orders.length > 0 &&
                            selectedOrders.size === orders.length
                          }
                        />
                      </th>
                      {safeVisibleColumns.frontorderid && (
                        <th className="pt-2 pb-2">Front Order ID</th>
                      )}
                      {safeVisibleColumns.orderType && (
                        <th className="pt-2 pb-2">Order Type</th>
                      )}
                      {safeVisibleColumns.orderStatus && (
                        <th className="pt-2 pb-2">Order Status</th>
                      )}
                      {safeVisibleColumns.orderSendBy && (
                        <th className="pt-2 pb-2">Order Send By</th>
                      )}
                      {safeVisibleColumns.rider && (
                        <th className="pt-2 pb-2">Rider</th>
                      )}
                      {safeVisibleColumns.invoiceNo && (
                        <th className="pt-2 pb-2">Invoice No</th>
                      )}
                      {safeVisibleColumns.source && (
                        <th className="pt-2 pb-2">Source</th>
                      )}
                      {safeVisibleColumns.tillname && (
                        <th className="pt-2 pb-2">Till Name</th>
                      )}
                      {safeVisibleColumns.customer && (
                        <th className="pt-2 pb-2">Customer</th>
                      )}
                      {safeVisibleColumns.orderDate && (
                        <th className="pt-2 pb-2">Order Date</th>
                      )}
                      {safeVisibleColumns.paymentStatus && (
                        <th className="pt-2 pb-2">Payment Status</th>
                      )}
                      {safeVisibleColumns.paymentMethod && (
                        <th className="pt-2 pb-2">Payment Method</th>
                      )}
                      {safeVisibleColumns.totalAmount && (
                        <th className="pt-2 pb-2">Total Amount</th>
                      )}
                      {safeVisibleColumns.productQty && (
                        <th className="pt-2 pb-2 text-center">Product Qty</th>
                      )}
                      {safeVisibleColumns.marketplaceWastageAmount && (
                        <th className="pt-2 pb-2">Wastage</th>
                      )}
                      {safeVisibleColumns.marketplaceDeliveryMode && (
                        <th className="pt-2 pb-2">Delivery Mode</th>
                      )}
                      {safeVisibleColumns.marketplaceDiscountFundedByPlatform && (
                        <th className="pt-2 pb-2">Discount (Platform)</th>
                      )}
                      {safeVisibleColumns.marketplaceVoucherFundedByPlatform && (
                        <th className="pt-2 pb-2">Voucher (Platform)</th>
                      )}
                      {safeVisibleColumns.marketplacePlatformCommissionAmount && (
                        <th className="pt-2 pb-2">Commission Amount</th>
                      )}
                      {safeVisibleColumns.marketplacePlatformCommissionPercent && (
                        <th className="pt-2 pb-2">Commission %</th>
                      )}
                      {safeVisibleColumns.marketplaceWastageRefundAmount && (
                        <th className="pt-2 pb-2">Wastage Refund</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            Object.keys(safeVisibleColumns).filter(
                              (key) => safeVisibleColumns[key]
                            ).length + 2
                          }
                          className="text-center"
                        >
                          No orders available.
                        </td>
                      </tr>
                    ) : (
                      orders.map((order, index) => (
                        <tr
                          key={order.orderID}
                          onClick={() => handleRowClick(order)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedOrders.has(order.recvorder_ID)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectOrder(
                                  order.recvorder_ID,
                                  index,
                                  e
                                );
                              }}
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          {safeVisibleColumns.frontorderid && (
                            <td className="align-middle">
                              {order.recvorder_FrontOrderID || "N/A"}
                            </td>
                          )}
                          {safeVisibleColumns.orderType && (
                            <td className="align-middle">
                              {order.recvorder_Ordertype === 'Dine-In' && order.t_Name
                                ? `${order.recvorder_Ordertype} (${order.t_Name})`
                                : order.recvorder_Ordertype}
                            </td>
                          )}
                          {safeVisibleColumns.orderStatus && (
                            <td className="align-middle">
                              {(() => {
                                const status =
                                  statusStyles[order.recvorder_status] ||
                                  statusStyles.default;
                                return (
                                  <span
                                    className={`badge border ${status.border} ${status.text} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                                  >
                                    <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                    {order.recvorder_status || status.label}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {safeVisibleColumns.orderSendBy && (
                            <td className="align-middle">
                              {order.sendByName || "N/A"}
                            </td>
                          )}
                          {safeVisibleColumns.rider && (
                            <td className="align-middle">
                              {order.assignedRiderDisplayName || order.AssignedRiderDisplayName || order.assignedRider || order.AssignedRider || "—"}
                            </td>
                          )}
                          {safeVisibleColumns.invoiceNo && (
                            <td className="align-middle">
                              {order.recvorder_InvoiceNo}
                            </td>
                          )}
                          {safeVisibleColumns.source && (
                            <td className="align-middle">
                              {order.recvorder_ReceivedFrom}
                            </td>
                          )}
                          {safeVisibleColumns.tillname && (
                            <td className="align-middle">
                              {order.tillname || "N/A"}
                            </td>
                          )}
                          {safeVisibleColumns.customer && (
                            <td className="align-middle">
                              {order.customerName || "N/A"}
                            </td>
                          )}
                          {safeVisibleColumns.orderDate && (
                            <td className="align-middle">
                              {order.recvorder_datetime
                                ? moment(order.recvorder_datetime).isValid()
                                  ? moment(order.recvorder_datetime).format(
                                    "DD MMM YYYY : HH:mm"
                                  )
                                  : "N/A"
                                : "N/A"}
                            </td>
                          )}
                          {safeVisibleColumns.paymentStatus && (
                            <td className="align-middle">
                              {(() => {
                                const paymentStatusValue = (order.recvorder_Paymentrecvstatus?.trim() || "").toLowerCase();
                                const displayValue = paymentStatusValue === "completed" ? "Paid" : paymentStatusValue === "pending" ? "Unpaid" : "Unpaid";
                                const status = paymentStatusStyles[displayValue] || paymentStatusStyles.default;
                                return (
                                  <span
                                    className={`badge border ${status.border} ${status.text} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                                  >
                                    <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                    {displayValue}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {safeVisibleColumns.paymentMethod && (
                            <td className="align-middle">
                              {(() => {
                                const paymentMethodValue =
                                  order.recvorder_Paymentmethod?.trim() || "";
                                const normalizedMethod = paymentMethodValue.toLowerCase();
                                const status =
                                  paymentMethodStyles[
                                  normalizedMethod === "card"
                                    ? "Card"
                                    : normalizedMethod === "cash"
                                      ? "Cash"
                                      : normalizedMethod === "online"
                                        ? "Online"
                                        : "default"
                                  ];
                                return (
                                  <span
                                    className={`badge border ${status.border} ${status.text} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                                  >
                                    <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                                    {paymentMethodValue || status.label}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {safeVisibleColumns.totalAmount && (
                            <td className="align-middle">
                              {getCurrencySymbol(compCurrency)}
                              {(order.recvorder_Totalamount || 0).toFixed(2)}
                            </td>
                          )}
                          {safeVisibleColumns.productQty && (
                            <td className="align-middle text-center">
                              {itemFilter.length > 0
                                ? formatProductQuantity(
                                    order.filteredProductQty ?? order.FilteredProductQty
                                  )
                                : "—"}
                            </td>
                          )}
                          {safeVisibleColumns.marketplaceWastageAmount && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_WastageAmount", formatMarketplaceCurrency)}
                            </td>
                          )}
                          {safeVisibleColumns.marketplaceDeliveryMode && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_DeliveryMode", formatMarketplaceText)}
                            </td>
                          )}
                          {safeVisibleColumns.marketplaceDiscountFundedByPlatform && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_DiscountFundedByPlatform", formatMarketplaceCurrency)}
                            </td>
                          )}
                          {safeVisibleColumns.marketplaceVoucherFundedByPlatform && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_VoucherFundedByPlatform", formatMarketplaceCurrency)}
                            </td>
                          )}
                          {safeVisibleColumns.marketplacePlatformCommissionAmount && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_PlatformCommissionAmount", formatMarketplaceCurrency)}
                            </td>
                          )}
                          {safeVisibleColumns.marketplacePlatformCommissionPercent && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_PlatformCommissionPercent", formatMarketplacePercent)}
                            </td>
                          )}
                          {safeVisibleColumns.marketplaceWastageRefundAmount && (
                            <td className="align-middle">
                              {renderMarketplaceGridValue(order, "marketplace_WastageRefundAmount", formatMarketplaceCurrency)}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2" onClick={(e) => e.stopPropagation()}>
                              {/* View Details Icon */}
                              <button
                                onClick={() => handleOpenModal(order)}
                                className="btn btn-sm btn-outline-info"
                                title="View Details"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #17a2b8',
                                  color: '#17a2b8',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#17a2b8';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#17a2b8';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-eye" style={{ fontSize: '12px' }}></i>
                              </button>

                              {/* Edit Icon - Hidden */}
                              {/* <Link
                                to={`/Order/orderDetails/${order.recvorder_InvoiceNo}`}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit Order"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = themeColor;
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                              </Link> */}

                              {/* Delete Icon - Only show for Completed or Draft orders */}
                              {(() => {
                                const orderStatus = order.recvorder_status?.trim() || "";
                                const canDelete = orderStatus === "Completed" || orderStatus === "Draft";
                                return canDelete ? (
                                  <button
                                    onClick={() => handleDelete(order)}
                                    className="btn btn-sm btn-outline-danger"
                                    title="Delete Order"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: '1px solid #dc3545',
                                      color: '#dc3545',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#dc3545';
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#dc3545';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                  </button>
                                ) : null;
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>

        {showModal && selectedOrder && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.85)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              transform: "translateZ(0)",
              willChange: "transform",
            }}
            onClick={handleCloseModal}
          >
            <div
              className="receipt-design"
              style={{
                fontSize: "14px",
                fontFamily: '"Courier New", Courier, monospace',
                width: "400px",
                background: "#ffffff",
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "20px 15px",
                lineHeight: "1.4",
                color: "#333",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                position: "relative",
                maxHeight: "80vh",
                overflowY: "auto",
                margin: "20px",
                zIndex: 10000,
                transform: "translateZ(0)",
                willChange: "transform",
                isolation: "isolate",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-1px",
                  left: "0",
                  right: "0",
                  height: "8px",
                  background:
                    "repeating-linear-gradient(90deg, #e0e0e0 0px, #e0e0e0 8px, transparent 8px, transparent 16px)",
                  borderRadius: "4px 4px 0 0",
                }}
              />

              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "6px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      letterSpacing: "1px",
                    }}
                  >
                    ORDER DETAILS
                  </div>
                  <button
                    onClick={handleCloseModal}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "16px",
                      cursor: "pointer",
                      color: "#666",
                      transition: "color 0.2s",
                    }}
                    onMouseOver={(e) => (e.target.style.color = "#007bff")}
                    onMouseOut={(e) => (e.target.style.color = "#666")}
                  >
                    &times;
                  </button>
                </div>

                <div style={{ fontSize: "12px", color: "#666", textAlign: "left" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Order ID:</span>
                    <span style={{ fontWeight: "bold" }}>
                      {detailedOrder?.recvorder_FrontOrderID || selectedOrder?.recvorder_FrontOrderID || "N/A"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Invoice No:</span>
                    <span style={{ fontWeight: "bold" }}>
                      {detailedOrder?.recvorder_InvoiceNo || selectedOrder?.recvorder_InvoiceNo || "N/A"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Source:</span>
                    <span>{detailedOrder?.recvorder_ReceivedFrom || selectedOrder?.recvorder_ReceivedFrom || "N/A"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Order Type:</span>
                    <span>
                      {(() => {
                        const orderType = detailedOrder?.recvorder_Ordertype || selectedOrder?.recvorder_Ordertype;
                        const tableName = detailedOrder?.t_name || selectedOrder?.t_Name;
                        if (orderType === 'Dine-In' && tableName) {
                          return `${orderType} (${tableName})`;
                        }
                        return orderType || "N/A";
                      })()}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Date and Time</span>
                    <span>
                      {(detailedOrder?.recvorder_datetime || selectedOrder?.recvorder_datetime) &&
                        moment(detailedOrder?.recvorder_datetime || selectedOrder?.recvorder_datetime).isValid()
                        ? moment(detailedOrder?.recvorder_datetime || selectedOrder?.recvorder_datetime).format("DD MMM YYYY h:mm A")
                        : "N/A"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Order Status:</span>
                    <span style={{
                      background: "#e9ecef",
                      color: "#6c757d",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      fontSize: "10px"
                    }}>
                      {detailedOrder?.recvorder_status || selectedOrder?.recvorder_status || "N/A"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Payment Status:</span>
                    <span style={{
                      background: "#e9ecef",
                      color: "#6c757d",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      fontSize: "10px"
                    }}>
                      {(detailedOrder?.recvorder_Paymentrecvstatus || selectedOrder?.recvorder_Paymentrecvstatus)?.toLowerCase() === "completed"
                        ? "Paid"
                        : (detailedOrder?.recvorder_Paymentrecvstatus || selectedOrder?.recvorder_Paymentrecvstatus)?.toLowerCase() === "pending"
                          ? "Unpaid"
                          : "Unpaid"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Payment Method:</span>
                    <span style={{
                      background: "#e9ecef",
                      color: "#6c757d",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      fontSize: "10px"
                    }}>
                      {(() => {
                        const paymentMethod = (detailedOrder?.recvorder_Paymentmethod || selectedOrder?.recvorder_Paymentmethod || "").toLowerCase();
                        if (paymentMethod.includes("mix") || (detailedOrder?.paymentDetails && detailedOrder.paymentDetails.length > 1) || (selectedOrder?.paymentDetails && selectedOrder.paymentDetails.length > 1)) {
                          return "MIX";
                        }
                        return detailedOrder?.recvorder_Paymentmethod || selectedOrder?.recvorder_Paymentmethod || "N/A";
                      })()}
                    </span>
                  </div>
                  {/* MIX Payment Details */}
                  {(() => {
                    const paymentMethod = (detailedOrder?.recvorder_Paymentmethod || selectedOrder?.recvorder_Paymentmethod || "").toLowerCase();
                    const isMixPayment = paymentMethod.includes("mix") || (detailedOrder?.paymentDetails && detailedOrder.paymentDetails.length > 1) || (selectedOrder?.paymentDetails && selectedOrder.paymentDetails.length > 1);
                    const paymentDetails = detailedOrder?.paymentDetails || selectedOrder?.paymentDetails || [];
                    
                    if (isMixPayment && paymentDetails.length > 0) {
                      return (
                        <div style={{ marginTop: "4px", marginLeft: "8px", fontSize: "10px", color: "#666" }}>
                          {paymentDetails.map((payment, idx) => (
                            <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                              <span>• {payment.paymentmethod || "N/A"}:</span>
                              <span>{getCurrencySymbol(compCurrency)}{(payment.amount || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Order Send By:</span>
                    <span>{detailedOrder?.sendByName || selectedOrder?.recvorder_OrderSendBy || "N/A"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span>Customer:</span>
                    <span>{detailedOrder?.customerName || selectedOrder?.customerName || selectedOrder?.customer_Name || "N/A"}</span>
                  </div>

                </div>
              </div>

              {(detailedOrder?.isMarketPlaceOrder || selectedOrder?.isMarketPlaceOrder) && (
                <>
                  <div style={{ borderTop: "1px dashed #999", margin: "12px 0" }} />
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>
                      MARKETPLACE SETTLEMENT
                    </div>
                    <div style={{ fontSize: "11px", color: "#555" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Wastage:</span>
                        <span>
                          {formatMarketplaceCurrency(
                            getMarketplaceField(detailedOrder, "marketplace_WastageAmount") ??
                              getMarketplaceField(selectedOrder, "marketplace_WastageAmount")
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Delivery Mode:</span>
                        <span>
                          {formatMarketplaceText(
                            getMarketplaceField(detailedOrder, "marketplace_DeliveryMode") ??
                              getMarketplaceField(selectedOrder, "marketplace_DeliveryMode")
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Discount (Platform):</span>
                        <span>
                          {formatMarketplaceCurrency(
                            getMarketplaceField(detailedOrder, "marketplace_DiscountFundedByPlatform") ??
                              getMarketplaceField(selectedOrder, "marketplace_DiscountFundedByPlatform")
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Voucher (Platform):</span>
                        <span>
                          {formatMarketplaceCurrency(
                            getMarketplaceField(detailedOrder, "marketplace_VoucherFundedByPlatform") ??
                              getMarketplaceField(selectedOrder, "marketplace_VoucherFundedByPlatform")
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Commission Amount:</span>
                        <span>
                          {formatMarketplaceCurrency(
                            getMarketplaceField(detailedOrder, "marketplace_PlatformCommissionAmount") ??
                              getMarketplaceField(selectedOrder, "marketplace_PlatformCommissionAmount")
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Commission %:</span>
                        <span>
                          {formatMarketplacePercent(
                            getMarketplaceField(detailedOrder, "marketplace_PlatformCommissionPercent") ??
                              getMarketplaceField(selectedOrder, "marketplace_PlatformCommissionPercent")
                          )}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                        <span>Wastage Refund:</span>
                        <span>
                          {formatMarketplaceCurrency(
                            getMarketplaceField(detailedOrder, "marketplace_WastageRefundAmount") ??
                              getMarketplaceField(selectedOrder, "marketplace_WastageRefundAmount")
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div style={{ borderTop: "1px dashed #999", margin: "12px 0" }} />

              {(() => {
                const deliveryAddress = detailedOrder?.recvOrder_DeliveryAddress || selectedOrder?.recvOrder_DeliveryAddress || customerDetails?.Cust_Address || "";
                const isDeliveryOrder = (detailedOrder?.recvorder_Ordertype || selectedOrder?.recvorder_Ordertype || "").toLowerCase() === "delivery";
                
                if (isDeliveryOrder && deliveryAddress && deliveryAddress.trim() !== "" && deliveryAddress !== "N/A") {
                  return (
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>
                        DELIVERY ADDRESS:
                      </div>
                      <div style={{ fontSize: "11px", color: "#555", lineHeight: "1.3" }}>
                        {deliveryAddress}
                      </div>
                      {(() => {
                        const riderName = detailedOrder?.AssignedRiderDisplayName || selectedOrder?.AssignedRiderDisplayName || detailedOrder?.assignedRiderDisplayName || selectedOrder?.assignedRiderDisplayName || detailedOrder?.AssignedRider || selectedOrder?.AssignedRider || detailedOrder?.assignedRider || selectedOrder?.assignedRider || "";
                        if (riderName && riderName.trim() !== "") {
                          return (
                            <div style={{ fontSize: "11px", color: "#555", marginTop: "4px", fontWeight: "600" }}>
                              Rider: {riderName}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                }
                return null;
              })()}

              {(() => {
                const deliveryAddress = detailedOrder?.recvOrder_DeliveryAddress || selectedOrder?.recvOrder_DeliveryAddress || customerDetails?.Cust_Address || "";
                const isDeliveryOrder = (detailedOrder?.recvorder_Ordertype || selectedOrder?.recvorder_Ordertype || "").toLowerCase() === "delivery";
                const hasDeliveryAddress = isDeliveryOrder && deliveryAddress && deliveryAddress.trim() !== "" && deliveryAddress !== "N/A";
                
                if (hasDeliveryAddress) {
                  return <div style={{ borderTop: "1px dashed #999", margin: "12px 0" }} />;
                }
                return null;
              })()}

              {(detailedOrder?.orderDetails || selectedOrder?.items || []).length > 0 && (
                <>
                  <div style={{ marginBottom: "8px" }}>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "25px 1fr 60px",
                      fontSize: "10px",
                      fontWeight: "bold",
                      borderBottom: "1px solid #ddd",
                      paddingBottom: "4px",
                      marginBottom: "8px"
                    }}>
                      <span>QTY</span>
                      <span>ITEM DESCRIPTION</span>
                      <span style={{ textAlign: "right" }}>TOTAL</span>
                    </div>

                    {(() => {
                      const orderItems = detailedOrder?.orderDetails || selectedOrder?.items || [];
                      const groupedItems = groupOrderItemsByModifierRelation(orderItems);
                      return groupedItems.map((group, groupIdx) => {
                        const item = group.main;
                        const variants = group.variants || [];
                        const modifiers = group.modifiers || [];
                        const discounts = group.discounts || [];
                        const itemQty = item.menuItem_Qty ?? item.MenuItem_Qty ?? 1;
                        const itemPrice = item.menuItem_Price ?? item.MenuItem_Price ?? 0;
                        const itemSubTotal = item.subTotal ?? item.SubTotal ?? (itemQty * itemPrice);
                        const itemName = item.menuItem_Name ?? item.MenuItem_Name ?? "N/A";
                        const detailId = item.recvorderDetail_ID ?? item.RecvorderDetail_ID ?? `group-${groupIdx}`;
                        const isComboItem = (item.menuItem_Type ?? item.MenuItem_Type ?? "").toUpperCase() === "COMBO";
                        const isVoid = item.isItemVoid ?? item.IsItemVoid ?? false;

                        return (
                          <div key={detailId} style={{ marginBottom: "12px" }}>
                            {/* Main Item */}
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "25px 1fr 60px",
                              fontSize: "11px",
                              alignItems: "start",
                              textDecoration: isVoid ? "line-through" : undefined,
                              opacity: isVoid ? 0.8 : undefined,
                              color: isVoid ? "#999" : undefined
                            }}>
                              <span style={{ fontWeight: "bold" }}>{itemQty}x</span>
                              <div>
                                <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
                                  {itemName}
                                  {isVoid && <span style={{ marginLeft: "4px", fontSize: "9px", background: "#999", color: "#fff", padding: "1px 4px", borderRadius: "3px", textDecoration: "none" }}>VOID</span>}
                                  {variants.length > 0 && (
                                    <span style={{ fontWeight: "normal", color: "#666" }}>
                                      {" "}({[...new Set(variants.map(v => v.menuItem_Name ?? v.MenuItem_Name))].join(", ")})
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span style={{ textAlign: "right", fontWeight: "bold" }}>
                                {getCurrencySymbol(compCurrency)}{itemSubTotal.toFixed(2)}
                              </span>
                            </div>

                            {/* Modifiers - Show each with qty and price */}
                            {modifiers.length > 0 && (
                              <div style={{ marginTop: "4px", marginLeft: "25px", paddingLeft: "8px", borderLeft: "2px solid #e0e0e0" }}>
                                <div style={{ fontSize: "9px", color: "#666", fontWeight: "600", marginBottom: "3px" }}>
                                  {isComboItem ? "Items:" : "Add-ons:"}
                                </div>
                                {modifiers.map((mod, modIdx) => {
                                  const modQty = mod.menuItem_Qty ?? mod.MenuItem_Qty ?? 1;
                                  const modPrice = mod.menuItem_Price ?? mod.MenuItem_Price ?? 0;
                                  const modName = mod.menuItem_Name ?? mod.MenuItem_Name ?? "N/A";
                                  const modVoid = mod.isItemVoid ?? mod.IsItemVoid ?? false;
                                  return (
                                    <div
                                      key={`mod-${groupIdx}-${modIdx}`}
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 50px",
                                        fontSize: "9px",
                                        color: modVoid ? "#999" : "#555",
                                        marginBottom: "2px",
                                        alignItems: "center",
                                        textDecoration: modVoid ? "line-through" : undefined,
                                        opacity: modVoid ? 0.8 : undefined
                                      }}
                                    >
                                      <span>
                                        <span style={{ fontWeight: "600" }}>{modQty}x</span> {modName}
                                      </span>
                                      <span style={{ textAlign: "right", fontWeight: "600" }}>
                                        {getCurrencySymbol(compCurrency)}{(modQty * modPrice).toFixed(2)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Item-level Discounts */}
                            {discounts.length > 0 && (
                              <div style={{ marginTop: "4px", marginLeft: "25px", paddingLeft: "8px", borderLeft: "2px solid #28a745" }}>
                                <div style={{ fontSize: "9px", color: "#28a745", fontWeight: "600", marginBottom: "3px" }}>
                                  Discount:
                                </div>
                                {discounts.map((disc, discIdx) => {
                                  const discAmount = disc.discountAmount ?? disc.DiscountAmount ?? 0;
                                  const discName = disc.menuItem_Name ?? disc.MenuItem_Name ?? "Discount";
                                  const discPct = disc.discountInPercentage ?? disc.DiscountInPercentage;
                                  const discVoid = disc.isItemVoid ?? disc.IsItemVoid ?? false;
                                  return (
                                    <div
                                      key={`disc-${groupIdx}-${discIdx}`}
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 50px",
                                        fontSize: "9px",
                                        color: discVoid ? "#999" : "#28a745",
                                        marginBottom: "2px",
                                        alignItems: "center",
                                        textDecoration: discVoid ? "line-through" : undefined,
                                        opacity: discVoid ? 0.8 : undefined
                                      }}
                                    >
                                      <span>
                                        {discName}
                                        {discPct != null && discPct > 0 && (
                                          <span style={{ color: "#666" }}> ({discPct}% off)</span>
                                        )}
                                      </span>
                                      <span style={{ textAlign: "right", fontWeight: "600" }}>
                                        -{getCurrencySymbol(compCurrency)}{discAmount.toFixed(2)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>

                  <div style={{ borderTop: "1px dashed #999", margin: "12px 0" }} />
                </>
              )}

              <div style={{ fontSize: "11px", marginBottom: "16px" }}>
                {(() => {
                  const discount = parseFloat(detailedOrder?.recvorder_Totaldiscount || selectedOrder?.recvorder_Totaldiscount || 0);
                  return discount > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span>Discount:</span>
                      <span>{getCurrencySymbol(compCurrency)}{discount.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const serviceCharges = parseFloat(detailedOrder?.recvorder_Servicecharges || selectedOrder?.recvorder_Servicecharges || 0);
                  return serviceCharges > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span>Service Charges:</span>
                      <span>{getCurrencySymbol(compCurrency)}{serviceCharges.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const vatAmount = parseFloat(detailedOrder?.vatAmount || selectedOrder?.vatAmount || 0);
                  return vatAmount > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span>VAT ({detailedOrder?.recvorder_VATType || selectedOrder?.recvorder_VATType || "N/A"}):</span>
                      <span>{getCurrencySymbol(compCurrency)}{vatAmount.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const tipAmount = parseFloat(detailedOrder?.recvorder_TipAmount || selectedOrder?.recvorder_TipAmount || 0);
                  return tipAmount > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span>Tip Amount:</span>
                      <span>{getCurrencySymbol(compCurrency)}{tipAmount.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const extraCharges = parseFloat(
                    detailedOrder?.recvorder_ExtraCharges || 
                    detailedOrder?.Recvorder_ExtraCharges || 
                    selectedOrder?.recvorder_ExtraCharges || 
                    selectedOrder?.Recvorder_ExtraCharges || 
                    0
                  );
                  return extraCharges > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span>Extra Charges:</span>
                      <span>{getCurrencySymbol(compCurrency)}{extraCharges.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const deliveryCharges = parseFloat(
                    detailedOrder?.recvorder_Deliverycharges || 
                    detailedOrder?.Recvorder_Deliverycharges || 
                    selectedOrder?.recvorder_Deliverycharges || 
                    selectedOrder?.Recvorder_Deliverycharges || 
                    0
                  );
                  return deliveryCharges > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span>Delivery Charges:</span>
                      <span>{getCurrencySymbol(compCurrency)}{deliveryCharges.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
              </div>

              <div style={{ borderTop: "2px solid #333", margin: "12px 0" }} />

              <div style={{ textAlign: "center", marginBottom: "16px", padding: "8px 0" }}>
                <div style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "4px" }}>
                  TOTAL AMOUNT
                </div>
                <div style={{ fontSize: "20px", fontWeight: "bold", letterSpacing: "1px" }}>
                  {getCurrencySymbol(compCurrency)}{(detailedOrder?.recvorder_Totalamount || selectedOrder?.recvorder_Totalamount || 0).toFixed(2)}
                </div>
              </div>

              {(() => {
                const order = detailedOrder || selectedOrder;
                const status = (
                  pickOrderField(order, "recvorder_status", "Recvorder_status") || ""
                )
                  .toString()
                  .trim()
                  .toLowerCase();
                const isRefunded =
                  selectedOrder?.isRefunded ||
                  status === "refunded" ||
                  status === "refundreturn" ||
                  status === "refund & return";
                const showCancel = hasCancelAudit(order);
                const cancelCode = pickOrderField(
                  order,
                  "cancelReasonCode",
                  "CancelReasonCode"
                );
                const cancelledByType = pickOrderField(
                  order,
                  "cancelledByType",
                  "CancelledByType"
                );
                const cancelledByDisplay = pickOrderField(
                  order,
                  "cancelledByDisplay",
                  "CancelledByDisplay"
                );
                const cancelledAt = pickOrderField(
                  order,
                  "cancelledAt",
                  "CancelledAt"
                );
                const freeTextReason = pickOrderField(
                  order,
                  "orderRejectorRefundReason",
                  "OrderRejectorRefundReason"
                );

                if (!showCancel && !isRefunded && !freeTextReason) return null;

                return (
                  <>
                    <div style={{ borderTop: "1px dashed #999", margin: "12px 0" }} />
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#dc3545",
                        textAlign: "left",
                        marginBottom: "8px",
                      }}
                    >
                      {isRefunded && (
                        <div style={{ fontWeight: "bold", marginBottom: "6px", textAlign: "center" }}>
                          REFUNDED: {getCurrencySymbol(compCurrency)}
                          {(
                            detailedOrder?.recvorder_Totalamount ||
                            selectedOrder?.grandTotal ||
                            0
                          ).toFixed(2)}
                        </div>
                      )}
                      {showCancel && (
                        <div style={{ fontWeight: "bold", marginBottom: "6px", textAlign: "center" }}>
                          {(status === "rejected" ? "REJECTED" : "CANCELLED").toUpperCase()}
                        </div>
                      )}
                      {cancelCode && (
                        <div style={{ fontSize: "10px", marginBottom: "2px" }}>
                          Reason code: {labelForCancelReasonCode(cancelCode)}
                          {labelForCancelReasonCode(cancelCode) !== cancelCode
                            ? ` (${cancelCode})`
                            : ""}
                        </div>
                      )}
                      {(cancelledByType || cancelledByDisplay) && (
                        <div style={{ fontSize: "10px", marginBottom: "2px" }}>
                          Cancelled by:{" "}
                          {[cancelledByType, cancelledByDisplay]
                            .filter(Boolean)
                            .join(" — ")}
                        </div>
                      )}
                      {cancelledAt && moment(cancelledAt).isValid() && (
                        <div style={{ fontSize: "10px", marginBottom: "2px" }}>
                          Cancelled at: {moment(cancelledAt).format("DD MMM YYYY h:mm A")}
                        </div>
                      )}
                      {freeTextReason && (
                        <div style={{ fontSize: "10px", marginBottom: "2px" }}>
                          Note: {freeTextReason}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              <div style={{ borderTop: "1px dashed #999", margin: "12px 0" }} />

              <div style={{
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "bold",
                letterSpacing: "2px",
                marginTop: "12px"
              }}>
                THANK YOU FOR YOUR ORDER!
              </div>
            </div>
          </div>
        )}

        <ProductMultiSelectionModal
          showModal={showItemFilterModal}
          setShowModal={setShowItemFilterModal}
          showButton={true}
          selectedProducts={itemFilter}
          setSelectedProducts={handleItemFilterSubmit}
          products={filterProducts}
          selectionPrompt="Select products to filter orders. Sold quantity includes combo items (item qty × combo qty)."
        />
      </div>
    </ErrorBoundary>
  );
};

export default ManageOrder;